import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import type {
  RenovationMediaKind,
  RenovationMediaUploadInput,
  RenovationMediaUpdateInput,
  RenovationMediaDto,
} from '@cc/types';

// Presigned URLs expire after 1h. Keep cached data fresh for 50 min so the
// next refetch happens before the URLs go stale. gcTime slightly longer so
// we don't evict mid-render.
const STALE_TIME_MS = 50 * 60 * 1000;
const GC_TIME_MS = 55 * 60 * 1000;

/**
 * Data hook for room media. Returns query + mutations the panel calls into.
 * `kind` is an optional server-side filter; pass null to show everything.
 */
export function useRoomMedia(args: {
  projectId: string;
  roomId: string | null;
  kind?: RenovationMediaKind | null;
}) {
  const queryClient = useQueryClient();
  const { projectId, roomId, kind } = args;

  const list = useQuery({
    queryKey: qk.roomMedia(roomId ?? '', kind ?? undefined),
    queryFn: () => api.listRoomMedia(projectId, roomId!, kind ?? undefined),
    enabled: Boolean(roomId),
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
  });

  function invalidate() {
    if (!roomId) return;
    // Invalidate every kind-filtered variant for this room.
    void queryClient.invalidateQueries({ queryKey: ['room-media', roomId] });
  }

  const upload = useMutation({
    mutationFn: (input: RenovationMediaUploadInput) => {
      if (!roomId) throw new Error('no room');
      return api.uploadRoomMedia(projectId, roomId, input);
    },
    onSuccess: () => {
      toast.success('Uploaded.');
      invalidate();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? mapUploadError(err)
          : 'Upload failed.';
      toast.error(msg);
    },
  });

  const update = useMutation({
    mutationFn: (args: { mediaId: string; patch: RenovationMediaUpdateInput }) => {
      if (!roomId) throw new Error('no room');
      return api.updateRoomMedia(projectId, roomId, args.mediaId, args.patch);
    },
    onSuccess: () => {
      toast.success('Saved.');
      invalidate();
    },
    onError: () => toast.error('Could not save.'),
  });

  const remove = useMutation({
    mutationFn: (mediaId: string) => {
      if (!roomId) throw new Error('no room');
      return api.deleteRoomMedia(projectId, roomId, mediaId);
    },
    onSuccess: () => {
      toast.success('Deleted.');
      invalidate();
    },
    onError: () => toast.error('Could not delete.'),
  });

  return {
    media: list.data?.media ?? ([] as RenovationMediaDto[]),
    isLoading: list.isLoading,
    isError: list.isError,
    invalidate,
    upload,
    update,
    remove,
  };
}

function mapUploadError(err: ApiError): string {
  if (err.code === 'too_large') return 'File too large (max 20 MB).';
  if (err.code === 'invalid_mime') return 'Unsupported file type. Use JPG, PNG, WebP, HEIC, GIF, or PDF.';
  if (err.code === 's3_not_configured') return 'Storage not configured.';
  if (err.code === 'persist_failed') return 'Saved to storage but DB write failed.';
  return err.message || 'Upload failed.';
}
